import { useMemo, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { SectionHeader } from '@/components/custom/companies/section-header'
import { MetricCombobox } from '@/components/custom/metrics/metric-combobox'
import {
  DEFAULT_FINANCIAL_METRIC_CARD_LABELS,
  useCompanyMetricLabelOptions,
  useExpandedMetricSeries,
  type CompanyMetricSeries,
  type ExpandedPeriodType,
  type FinancialMetricOption,
  type MetricPoint,
} from '@/hooks/use-metrics'
import type { CompanyMetricsView } from '@/services/metrics.service'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'

interface CompositeMetricChartProps {
  companyId: string
}

type ChartDatum = {
  quarter: string
  quarterKey: string
  periodEndDate: string
  bar: number | null
  line: number | null
}

const PERIOD_OPTIONS: { value: ExpandedPeriodType; label: string }[] = [
  { value: 'LTM', label: 'LTM' },
  { value: 'QUARTERLY', label: 'Quarter' },
  { value: 'MONTHLY', label: 'Month' },
  { value: 'YTD', label: 'YTD' },
]

const EMPTY_METRIC_OPTIONS: FinancialMetricOption[] = []
const FALLBACK_METRIC_LABEL = 'Select metric'

const BAR_COLOR = '#1a4878'
const BAR_COLOR_NEGATIVE = 'var(--destructive, #c0392b)'
const LINE_COLOR = '#b86846'
const GRID_STROKE = 'color-mix(in srgb, var(--primary) 10%, transparent)'
const ZERO_LINE_STROKE = 'color-mix(in srgb, var(--primary) 14%, transparent)'

const chartConfig = {
  bar: { label: 'Bar', color: BAR_COLOR },
  line: { label: 'Line', color: LINE_COLOR },
} satisfies ChartConfig

const selectTriggerClassName =
  'h-9 w-full cursor-pointer rounded-sm border-primary/20 bg-white px-3 text-sm font-normal leading-5 shadow-none sm:w-auto [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-60'

function isPeriod(value: string): value is ExpandedPeriodType {
  return (
    value === 'LTM' ||
    value === 'QUARTERLY' ||
    value === 'MONTHLY' ||
    value === 'YTD'
  )
}

function isView(value: string): value is CompanyMetricsView {
  return value === 'MONTHLY' || value === 'QUARTERLY' || value === 'ANNUAL'
}

function findOptionByLabeledAs(
  options: FinancialMetricOption[],
  labeledAs: string
): FinancialMetricOption | undefined {
  return options.find((opt) => opt.labeledAs === labeledAs)
}

function buildChartData(
  barPoints: MetricPoint[],
  linePoints: MetricPoint[]
): ChartDatum[] {
  // Bars need contiguous spacing on a regular cadence, so whichever series
  // has more points defines the x-axis spine. The other series maps onto
  // those keys; orphan points outside the spine are dropped to avoid empty
  // columns at the edges.
  const spinePoints =
    barPoints.length >= linePoints.length ? barPoints : linePoints

  const barByKey = new Map(barPoints.map((p) => [p.quarterKey, p]))
  const lineByKey = new Map(linePoints.map((p) => [p.quarterKey, p]))

  return spinePoints.map((spinePoint) => {
    const barPoint = barByKey.get(spinePoint.quarterKey)
    const linePoint = lineByKey.get(spinePoint.quarterKey)
    return {
      quarter: spinePoint.quarter,
      quarterKey: spinePoint.quarterKey,
      periodEndDate: spinePoint.periodEndDate,
      bar: barPoint?.value ?? null,
      line: linePoint?.value ?? null,
    }
  })
}

function CompositeTooltip({
  active,
  payload,
  label,
  barLabel,
  lineLabel,
}: {
  active?: boolean
  payload?: Array<{ dataKey: string; value: number | null }>
  label?: string
  barLabel: string
  lineLabel: string
}) {
  if (!active || !payload?.length) return null

  const bar = payload.find((p) => p.dataKey === 'bar')?.value ?? null
  const line = payload.find((p) => p.dataKey === 'line')?.value ?? null

  return (
    <ChartTooltipContent className="pointer-events-none border-border/40 px-2 py-1.5 shadow-lg">
      <div className="space-y-1">
        <div className="text-primary/70">{label}</div>
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-sm"
              style={{ backgroundColor: BAR_COLOR }}
            />
            <span className="text-primary/70">{barLabel}</span>
          </span>
          <span
            className={cn(
              'font-medium tabular-nums',
              bar != null && bar < 0 ? 'text-destructive' : 'text-primary'
            )}
          >
            {formatCurrency(bar)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: LINE_COLOR }}
            />
            <span className="text-primary/70">{lineLabel}</span>
          </span>
          <span
            className={cn(
              'font-medium tabular-nums',
              line != null && line < 0 ? 'text-destructive' : 'text-primary'
            )}
          >
            {formatCurrency(line)}
          </span>
        </div>
      </div>
    </ChartTooltipContent>
  )
}

export interface CompositeChartState {
  period: ExpandedPeriodType
  setPeriod: (p: ExpandedPeriodType) => void
  view: CompanyMetricsView
  setView: (v: CompanyMetricsView) => void
  rangeStart: string | undefined
  setRangeStart: (periodEndDate: string | undefined) => void
  rangeEnd: string | undefined
  setRangeEnd: (periodEndDate: string | undefined) => void
  barMetricId: string | undefined
  setBarMetricId: (id: string) => void
  lineMetricId: string | undefined
  setLineMetricId: (id: string) => void
  effectiveBarMetricId: string | undefined
  effectiveLineMetricId: string | undefined
}

export function useCompositeChartState(
  metricOptions: FinancialMetricOption[]
): CompositeChartState {
  const [period, setPeriod] = useState<ExpandedPeriodType>('LTM')
  const [view, setView] = useState<CompanyMetricsView>('QUARTERLY')
  const [rangeStart, setRangeStart] = useState<string | undefined>(undefined)
  const [rangeEnd, setRangeEnd] = useState<string | undefined>(undefined)
  const [barMetricId, setBarMetricId] = useState<string | undefined>(undefined)
  const [lineMetricId, setLineMetricId] = useState<string | undefined>(
    undefined
  )

  const effectiveBarMetricId = useMemo(() => {
    if (barMetricId) return barMetricId
    return findOptionByLabeledAs(
      metricOptions,
      DEFAULT_FINANCIAL_METRIC_CARD_LABELS.revenue
    )?.id
  }, [barMetricId, metricOptions])

  const effectiveLineMetricId = useMemo(() => {
    if (lineMetricId) return lineMetricId
    return findOptionByLabeledAs(
      metricOptions,
      DEFAULT_FINANCIAL_METRIC_CARD_LABELS.ebitda
    )?.id
  }, [lineMetricId, metricOptions])

  const setPeriodAndResetRange = (nextPeriod: ExpandedPeriodType) => {
    setPeriod(nextPeriod)
    setRangeStart(undefined)
    setRangeEnd(undefined)
  }

  const setViewAndResetRange = (nextView: CompanyMetricsView) => {
    setView(nextView)
    setRangeStart(undefined)
    setRangeEnd(undefined)
  }

  return {
    period,
    setPeriod: setPeriodAndResetRange,
    view,
    setView: setViewAndResetRange,
    rangeStart,
    setRangeStart,
    rangeEnd,
    setRangeEnd,
    barMetricId,
    setBarMetricId,
    lineMetricId,
    setLineMetricId,
    effectiveBarMetricId,
    effectiveLineMetricId,
  }
}

export interface CompositeMetricChartLayoutProps {
  metricOptions: FinancialMetricOption[]
  state: CompositeChartState
  barSeries: CompanyMetricSeries | undefined
  lineSeries: CompanyMetricSeries | undefined
  isFetching: boolean
}

export function CompositeMetricChartLayout({
  metricOptions,
  state,
  barSeries,
  lineSeries,
  isFetching,
}: CompositeMetricChartLayoutProps) {
  const {
    period,
    setPeriod,
    view,
    setView,
    setBarMetricId,
    setLineMetricId,
    effectiveBarMetricId,
    effectiveLineMetricId,
    rangeStart,
    setRangeStart,
    rangeEnd,
    setRangeEnd,
  } = state

  const barLabel = useMemo(
    () =>
      metricOptions.find((opt) => opt.id === effectiveBarMetricId)?.label ??
      FALLBACK_METRIC_LABEL,
    [metricOptions, effectiveBarMetricId]
  )
  const lineLabel = useMemo(
    () =>
      metricOptions.find((opt) => opt.id === effectiveLineMetricId)?.label ??
      FALLBACK_METRIC_LABEL,
    [metricOptions, effectiveLineMetricId]
  )

  const chartData = useMemo(
    () => buildChartData(barSeries?.points ?? [], lineSeries?.points ?? []),
    [barSeries, lineSeries]
  )

  const rangeOptions = useMemo(
    () =>
      chartData.map((point) => ({
        value: point.periodEndDate,
        label: point.quarter,
      })),
    [chartData]
  )

  const rangeValues = useMemo(
    () => new Set(rangeOptions.map((option) => option.value)),
    [rangeOptions]
  )

  const firstRangeValue = rangeOptions[0]?.value
  const lastRangeValue = rangeOptions[rangeOptions.length - 1]?.value
  const selectedRangeStart =
    rangeStart && rangeValues.has(rangeStart) ? rangeStart : firstRangeValue
  const selectedRangeEnd =
    rangeEnd && rangeValues.has(rangeEnd) ? rangeEnd : lastRangeValue

  const selectedRangeStartIndex = rangeOptions.findIndex(
    (option) => option.value === selectedRangeStart
  )
  const selectedRangeEndIndex = rangeOptions.findIndex(
    (option) => option.value === selectedRangeEnd
  )

  const filteredChartData = useMemo(() => {
    if (selectedRangeStartIndex < 0 || selectedRangeEndIndex < 0) {
      return chartData
    }

    const start = Math.min(selectedRangeStartIndex, selectedRangeEndIndex)
    const end = Math.max(selectedRangeStartIndex, selectedRangeEndIndex)
    return chartData.slice(start, end + 1)
  }, [chartData, selectedRangeEndIndex, selectedRangeStartIndex])

  const handleRangeStartChange = (nextValue: string) => {
    setRangeStart(nextValue)
    const nextStartIndex = rangeOptions.findIndex(
      (option) => option.value === nextValue
    )
    if (nextStartIndex > selectedRangeEndIndex) {
      setRangeEnd(nextValue)
    }
  }

  const handleRangeEndChange = (nextValue: string) => {
    setRangeEnd(nextValue)
    const nextEndIndex = rangeOptions.findIndex(
      (option) => option.value === nextValue
    )
    if (nextEndIndex < selectedRangeStartIndex) {
      setRangeStart(nextValue)
    }
  }

  const hasData = filteredChartData.some((d) => d.bar != null || d.line != null)

  const hasNegativeBar = filteredChartData.some(
    (d) => d.bar != null && d.bar < 0
  )
  const hasNegativeLine = filteredChartData.some(
    (d) => d.line != null && d.line < 0
  )
  const hasRangeOptions = rangeOptions.length > 0

  return (
    <section className="space-y-3">
      <SectionHeader title="Metric Comparison" />

      <div className="rounded-sm border border-border bg-white p-4">
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto_auto_auto] xl:items-end">
          <div className="flex flex-col gap-1">
            <Typography
              variant="body-sm"
              className="text-xs uppercase tracking-wide text-primary/70"
            >
              Bars (Left Axis)
            </Typography>
            <MetricCombobox
              options={metricOptions}
              value={effectiveBarMetricId}
              disabledValue={effectiveLineMetricId}
              onValueChange={setBarMetricId}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Typography
              variant="body-sm"
              className="text-xs uppercase tracking-wide text-primary/70"
            >
              Line (Right Axis)
            </Typography>
            <MetricCombobox
              options={metricOptions}
              value={effectiveLineMetricId}
              disabledValue={effectiveBarMetricId}
              onValueChange={setLineMetricId}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Typography
              variant="body-sm"
              className="text-xs uppercase tracking-wide text-primary/70"
            >
              Trailing Period
            </Typography>
            <Select
              value={period}
              onValueChange={(v) => {
                if (isPeriod(v)) setPeriod(v)
              }}
            >
              <SelectTrigger
                className={selectTriggerClassName}
                aria-label="Trailing period"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-32">
                {PERIOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Typography
              variant="body-sm"
              className="text-xs uppercase tracking-wide text-primary/70"
            >
              Frequency
            </Typography>
            <Select
              value={view}
              onValueChange={(v) => {
                if (isView(v)) setView(v)
              }}
            >
              <SelectTrigger
                className={selectTriggerClassName}
                aria-label="Frequency"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-32">
                <SelectItem value="MONTHLY">Monthly</SelectItem>
                <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                <SelectItem value="ANNUAL">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Typography
              variant="body-sm"
              className="text-xs uppercase tracking-wide text-primary/70"
            >
              Start
            </Typography>
            <Select
              value={selectedRangeStart ?? '__none'}
              onValueChange={handleRangeStartChange}
              disabled={!hasRangeOptions}
            >
              <SelectTrigger
                className={selectTriggerClassName}
                aria-label="Start period"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-32">
                {hasRangeOptions ? (
                  rangeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="__none" disabled>
                    —
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Typography
              variant="body-sm"
              className="text-xs uppercase tracking-wide text-primary/70"
            >
              End
            </Typography>
            <Select
              value={selectedRangeEnd ?? '__none'}
              onValueChange={handleRangeEndChange}
              disabled={!hasRangeOptions}
            >
              <SelectTrigger
                className={selectTriggerClassName}
                aria-label="End period"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-32">
                {hasRangeOptions ? (
                  rangeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="__none" disabled>
                    —
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isFetching && filteredChartData.length === 0 ? (
          <Skeleton className="h-72 w-full rounded-sm" />
        ) : !hasData ? (
          <div className="flex h-72 items-center justify-center">
            <Typography variant="body-sm" className="text-primary/70">
              No data available for this period
            </Typography>
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="h-72 w-full aspect-auto [&_.recharts-tooltip-cursor]:fill-transparent"
          >
            <ComposedChart
              data={filteredChartData}
              margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
              barCategoryGap={filteredChartData.length > 12 ? '10%' : '20%'}
            >
              <CartesianGrid vertical={false} stroke={GRID_STROKE} />
              {(hasNegativeBar || hasNegativeLine) && (
                <ReferenceLine
                  y={0}
                  stroke={ZERO_LINE_STROKE}
                  strokeWidth={1}
                />
              )}
              <XAxis
                dataKey="quarter"
                axisLine={false}
                tickLine={false}
                tickMargin={6}
                interval={filteredChartData.length > 12 ? 1 : 0}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
                  fontSize: 10,
                }}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
                  fontSize: 10,
                }}
                tickFormatter={(v: number) => formatCurrency(v)}
                width={60}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
                  fontSize: 10,
                }}
                tickFormatter={(v: number) => formatCurrency(v)}
                width={60}
              />
              <ChartTooltip
                content={
                  <CompositeTooltip barLabel={barLabel} lineLabel={lineLabel} />
                }
              />
              <Bar
                yAxisId="left"
                dataKey="bar"
                maxBarSize={56}
                radius={[4, 4, 0, 0]}
              >
                {filteredChartData.map((d, i) => (
                  <Cell
                    key={`bar-${i}`}
                    fill={
                      d.bar != null && d.bar < 0
                        ? BAR_COLOR_NEGATIVE
                        : BAR_COLOR
                    }
                  />
                ))}
              </Bar>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="line"
                stroke={LINE_COLOR}
                strokeWidth={2}
                dot={{ r: 3, fill: LINE_COLOR }}
                activeDot={{ r: 4 }}
                connectNulls
              />
            </ComposedChart>
          </ChartContainer>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pt-3">
          <div className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-sm"
              style={{ backgroundColor: BAR_COLOR }}
            />
            <span className="font-medium text-primary/80">{barLabel}</span>
            <span className="text-primary/70">(left)</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block h-0.5 w-3 shrink-0 rounded-sm"
              style={{ backgroundColor: LINE_COLOR }}
            />
            <span className="font-medium text-primary/80">{lineLabel}</span>
            <span className="text-primary/70">(right)</span>
          </div>
        </div>
      </div>
    </section>
  )
}

export function CompositeMetricChart({ companyId }: CompositeMetricChartProps) {
  const { data } = useCompanyMetricLabelOptions(companyId)
  const allMetricOptions = data ?? EMPTY_METRIC_OPTIONS

  // KPIs are mixed-unit (counts, percentages, ratios) and don't share an axis
  // with financial metrics, so they're not meaningful in this combo chart.
  // Keep them visible in the table tab only. Backend already drops labels
  // that have no non-zero observations, so no extra "has data" filter here.
  const metricOptions = useMemo(
    () => allMetricOptions.filter((opt) => opt.category !== 'KPI'),
    [allMetricOptions]
  )

  const state = useCompositeChartState(metricOptions)
  const { period, view, effectiveBarMetricId, effectiveLineMetricId } = state

  const { series: barSeries, isFetching: barFetching } =
    useExpandedMetricSeries(
      companyId,
      effectiveBarMetricId,
      period,
      Boolean(effectiveBarMetricId),
      view
    )
  const { series: lineSeries, isFetching: lineFetching } =
    useExpandedMetricSeries(
      companyId,
      effectiveLineMetricId,
      period,
      Boolean(effectiveLineMetricId),
      view
    )

  return (
    <CompositeMetricChartLayout
      metricOptions={metricOptions}
      state={state}
      barSeries={barSeries}
      lineSeries={lineSeries}
      isFetching={barFetching || lineFetching}
    />
  )
}
